/**
 * C:\Sovereign\Projects\Memorials\memorial-intake\src\lib\api.ts
 *
 * Sovereign Worker API Client
 * Typed fetch wrappers aligned to the deployed Cloudflare Worker contract.
 *
 * Endpoints (worker/src/routes/upload.ts):
 *   POST /api/upload/presign   — get a pre-signed PUT URL for R2 direct upload
 *   POST /api/upload/complete  — submit completed manifest to D1 + fire webhook
 *
 * ZERO tRPC. ZERO Express. ZERO cloud dependencies.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type AssetType = "photos" | "videos" | "audio";

/** Matches worker/src/routes/upload.ts handlePresign request body */
export interface PresignRequest {
  projectId:    string;
  filename:     string;
  contentType:  string;
  fileCategory: AssetType;
}

/** Matches worker/src/routes/upload.ts handlePresign response — field names are exact */
export interface PresignResponse {
  url:       string;   // pre-signed PUT URL for R2 direct upload
  objectKey: string;   // R2 object key: <tenant>/<projectId>/<fileCategory>/<filename>
  tenant:    string;
  bucket:    string;
  expiresIn: number;
}

/** Manifest passed to /api/upload/complete — matches handleComplete body.manifest */
export interface IntakeManifest {
  firstName:       string;
  lastName:        string;
  dateOfBirth:     string;
  dateOfPassing:   string;
  tagline:         string;
  tier:            string;
  tone:            string;
  musicStyle:      string;
  musicTrack:      string;
  contactName:     string;
  contactEmail:    string;
  contactPhone:    string;
  photoOrderNotes: string;
  photoKeys:       string[];   // objectKeys of uploaded photos
  videoKeys:       string[];   // objectKeys of uploaded videos
  audioKeys:       string[];   // objectKeys of uploaded audio
}

/** Matches worker/src/routes/upload.ts handleComplete request body */
export interface CompleteRequest {
  projectId: string;
  manifest:  IntakeManifest;
}

/** Matches worker/src/routes/upload.ts handleComplete response */
export interface CompleteResponse {
  success:   boolean;
  projectId: string;
  tenant:    string;
  message:   string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Always use relative paths — Vite dev proxy forwards /api/* to localhost:8787 */
function baseUrl(): string {
  return "";
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${baseUrl()}${path}`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(body),
  });

  if (!res.ok) {
    let message = `Worker API error ${res.status}`;
    try {
      const err = await res.json() as { error?: string; message?: string };
      message = err.error ?? err.message ?? message;
    } catch { /* ignore parse failure */ }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

// ── API Surface ───────────────────────────────────────────────────────────────

/**
 * Request a pre-signed PUT URL for a single file.
 * The browser then PUTs the file bytes directly to R2 — no bytes through the Worker.
 * Returns { url, objectKey, tenant, bucket, expiresIn }.
 */
export async function getPresignedUrl(req: PresignRequest): Promise<PresignResponse> {
  return post<PresignResponse>("/api/upload/presign", req);
}

/**
 * Submit the completed intake manifest to D1 and fire the sovereign webhook.
 * All file assets must already be uploaded to R2 before calling this.
 * The Worker upserts the D1 row and dispatches to local-orchestrator:8080.
 */
export async function completeIntake(req: CompleteRequest): Promise<CompleteResponse> {
  return post<CompleteResponse>("/api/upload/complete", req);
}
